export interface IDoorOpenPeriod {
  start_date: string
  end_date: string | null
  duration?: string | number | null
}

export function periodEndMs(period: IDoorOpenPeriod, rangeEndMs: number): number {
  return period.end_date ? new Date(period.end_date).getTime() : rangeEndMs
}

export function periodsOverlap(
  a: IDoorOpenPeriod,
  b: IDoorOpenPeriod,
  rangeEndMs: number,
): boolean {
  const aStart = new Date(a.start_date).getTime()
  const aEnd = periodEndMs(a, rangeEndMs)
  const bStart = new Date(b.start_date).getTime()
  const bEnd = periodEndMs(b, rangeEndMs)
  return aStart < bEnd && bStart < aEnd
}

export function periodDurationMinutes(period: IDoorOpenPeriod, rangeEndMs?: number): number {
  if (typeof period.duration === 'number') return period.duration
  if (typeof period.duration === 'string' && period.duration.trim() !== '') {
    const daysMatch = period.duration.match(/(\d+)\s*d/)
    const hoursMatch = period.duration.match(/(\d+)\s*h/)
    const minMatch = period.duration.match(/(\d+)\s*min/)
    const days = daysMatch ? parseInt(daysMatch[1], 10) : 0
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0
    const minutes = minMatch ? parseInt(minMatch[1], 10) : 0
    if (days > 0 || hours > 0 || minutes > 0) return days * 1440 + hours * 60 + minutes
  }
  if (!period.start_date) return 0
  const endMs = period.end_date ? new Date(period.end_date).getTime() : rangeEndMs
  if (endMs == null) return 0
  const diffMs = Math.max(0, endMs - new Date(period.start_date).getTime())
  return Math.floor(diffMs / 60000)
}

/** Remove períodos de `candidates` que sobrepõem qualquer item em `reference` (referência prevalece). */
export function excludeOverlappingPeriods(
  candidates: IDoorOpenPeriod[],
  reference: IDoorOpenPeriod[],
  rangeEndMs: number,
): IDoorOpenPeriod[] {
  if (reference.length === 0) return [...candidates]
  return candidates.filter(
    (candidate) => !reference.some((ref) => periodsOverlap(candidate, ref, rangeEndMs)),
  )
}

export function hasDoorOpenAlarmConfig(
  maxSetpoint: number | null | undefined,
  debounceSetpoint: number | null | undefined,
): boolean {
  return maxSetpoint != null && debounceSetpoint != null
}

/**
 * Deriva aberturas curtas a partir de `time_door_open`, sem overlap com `open_periods`.
 * Com setpoint: mantém apenas duração < maxSetpointMinutes.
 * Sem setpoint: retorna todas as aberturas detectadas (ainda excluindo overlaps com open_periods).
 */
export function buildShortOpenPeriods(
  timeDoorOpen: IDoorOpenPeriod[],
  alarmWorthyPeriods: IDoorOpenPeriod[],
  maxSetpointMinutes: number | null,
  rangeEndMs: number,
  withAlarmConfig: boolean,
): IDoorOpenPeriod[] {
  const withoutOverlap = excludeOverlappingPeriods(timeDoorOpen, alarmWorthyPeriods, rangeEndMs)
  if (!withAlarmConfig || maxSetpointMinutes == null) return withoutOverlap
  return withoutOverlap.filter(
    (period) => periodDurationMinutes(period, rangeEndMs) < maxSetpointMinutes,
  )
}

export function parseTimeDoorOpen(readings: Record<string, unknown> | undefined): IDoorOpenPeriod[] {
  const raw = readings?.time_door_open
  return Array.isArray(raw) ? (raw as IDoorOpenPeriod[]) : []
}

export type DoorOpenChartPoint = [number, number, string | null]

export interface IDoorOpenVoltageReading {
  t?: string
  v?: number
  s?: string
}

/** Mapeia readings `door_open_voltage` para pontos do gráfico step (Aberta=0, Fechada=1). */
export function mapDoorOpenVoltageToChartData(
  voltageReadings: IDoorOpenVoltageReading[],
): DoorOpenChartPoint[] {
  if (voltageReadings.length === 0) return []

  return voltageReadings
    .map((r) => {
      const timeStr = r.t
      const status = r.s || null
      const val = status === 'open' ? 0 : status === 'closed' ? 1 : null
      if (!timeStr || val === null) return [0, 0, null] as DoorOpenChartPoint
      return [new Date(timeStr).getTime(), val, status] as DoorOpenChartPoint
    })
    .filter(([t]) => t > 0)
}

/**
 * Converte leituras de status (open/closed) em períodos contínuos de porta aberta.
 * Usado como fallback quando `time_door_open` não vem na API.
 */
export function mapDoorOpenVoltageToOpenPeriods(
  voltageReadings: IDoorOpenVoltageReading[],
  rangeEndMs: number,
): IDoorOpenPeriod[] {
  const sorted = voltageReadings
    .map((reading) => {
      const time = reading.t ? new Date(reading.t).getTime() : NaN
      const status = reading.s === 'open' || reading.s === 'closed' ? reading.s : null
      return { time, status }
    })
    .filter((reading) => Number.isFinite(reading.time) && reading.status != null)
    .sort((a, b) => a.time - b.time)

  if (sorted.length === 0) return []

  const periods: IDoorOpenPeriod[] = []
  let openStart: number | null = null

  for (const reading of sorted) {
    if (reading.status === 'open') {
      if (openStart == null) openStart = reading.time
      continue
    }

    if (openStart != null) {
      periods.push({
        start_date: new Date(openStart).toISOString(),
        end_date: new Date(reading.time).toISOString(),
      })
      openStart = null
    }
  }

  if (openStart != null) {
    periods.push({
      start_date: new Date(openStart).toISOString(),
      end_date: new Date(rangeEndMs).toISOString(),
    })
  }

  return periods
}
